import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Search, ArrowUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { writeAuditLog } from '@/lib/audit';
import { getStatusColor, debounce, cn } from '@/lib/utils';
import { APMBadge } from '@/components/APMBadge';
import { useListNavigationStore } from '@/lib/useListNavigation';
import { fetchBuildStock } from '@/lib/api/buildStock';

type SortField = 'sku' | 'description' | 'status' | 'units_built' | 'in_stock' | 'max_buildable' | 'apm_score' | 'date_created';
type SortOrder = 'asc' | 'desc';

const STATUS_OPTIONS = [
  { value: 'Active',    label: 'Active' },
  { value: 'Inactive',  label: 'Inactive' },
];

export function BuildsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read filter state from URL params (survives back-navigation)
  const search = searchParams.get('q') || '';
  const [searchInput, setSearchInput] = useState(search);
  const statusFilter = searchParams.get('status') || '';
  const page = Number(searchParams.get('page')) || 1;
  const pageSizeParam = searchParams.get('pageSize');
  const pageSize: number | 'all' = pageSizeParam === 'all' ? 'all' : (Number(pageSizeParam) || 25);
  const sortField = (searchParams.get('sort') as SortField) || 'sku';
  const sortOrder = (searchParams.get('order') as SortOrder) || 'asc';

  // Helper to update URL params — batches all changes into one update
  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === '') next.delete(k);
        else next.set(k, v);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Fetch ALL builds once, compute everything client-side, then sort/filter/paginate
  const { data: allBuildsData, isLoading } = useQuery({
    queryKey: ['builds-all'],
    queryFn: async () => {
      const { data: builds, error } = await supabase
        .from('builds')
        .select('id, sku, description, status, units_built, date_created, comments')
        .order('sku', { ascending: true });
      if (error) throw error;

      const buildIds = (builds ?? []).map((b: any) => b.id);
      const buildSkuMap: Record<string, string> = {};
      (builds ?? []).forEach((b: any) => { buildSkuMap[b.id] = b.sku; });
      const maxBuildableMap: Record<string, number | null> = {};
      const KEY_CATEGORIES = new Set(['cpu', 'gpu', 'case', 'cas']);
      const bomByBuild: Record<string, { sku: string; qty: number; type: string }[]> = {};
      const stockFullMap: Record<string, { available: number; reserved: number; on_order: number; expected: number; category: string }> = {};

      if (buildIds.length > 0) {
        // 1. Try build_components table first
        const { data: bomRows } = await supabase
          .from('build_components')
          .select('build_id, component_sku, qty_per_build, component_type')
          .in('build_id', buildIds);

        (bomRows ?? []).forEach((r: any) => {
          if (!bomByBuild[r.build_id]) bomByBuild[r.build_id] = [];
          bomByBuild[r.build_id].push({ sku: r.component_sku, qty: r.qty_per_build ?? 1, type: (r.component_type || '').toLowerCase() });
        });

        // 2. Fallback: for builds without build_components rows, use components.build_sku
        const buildsWithoutBom = buildIds.filter(id => !bomByBuild[id] || bomByBuild[id].length === 0);
        if (buildsWithoutBom.length > 0) {
          const skusToSearch = new Set(buildsWithoutBom.map(id => buildSkuMap[id]).filter(Boolean));
          if (skusToSearch.size > 0) {
            // Fetch ALL components with build_sku set (paginate to avoid 1000-row default limit)
            let allFallbackComps: any[] = [];
            let from = 0;
            const PAGE = 1000;
            while (true) {
              const { data: page } = await supabase
                .from('components')
                .select('part_number, description, category, current_price, build_sku, available_qty, on_order_qty, reserved_qty, expected_qty')
                .neq('build_sku', '')
                .not('build_sku', 'is', null)
                .range(from, from + PAGE - 1);
              if (!page || page.length === 0) break;
              allFallbackComps = allFallbackComps.concat(page);
              if (page.length < PAGE) break;
              from += PAGE;
            }

            for (const bid of buildsWithoutBom) {
              const bSku = buildSkuMap[bid];
              if (!bSku) continue;
              const matched = allFallbackComps.filter((c: any) => {
                const entries = (c.build_sku ?? '').split(',').map((s: string) => s.trim().replace(/-EOL$/i, ''));
                return entries.includes(bSku);
              });
              if (matched.length > 0) {
                bomByBuild[bid] = matched.map((c: any) => ({
                  sku: c.part_number,
                  qty: 1,
                  type: (c.category || '').toLowerCase(),
                }));
                matched.forEach((c: any) => {
                  stockFullMap[c.part_number] = {
                    available: c.available_qty ?? 0,
                    reserved: c.reserved_qty ?? 0,
                    on_order: c.on_order_qty ?? 0,
                    expected: c.expected_qty ?? 0,
                    category: (c.category || '').toLowerCase(),
                  };
                });
              }
            }
          }
        }

        // 3. Fetch stock for build_components SKUs not yet in stockFullMap
        const allSkus = [...new Set(
          Object.values(bomByBuild).flat().map(r => r.sku).filter(s => s && !stockFullMap[s])
        )];
        if (allSkus.length > 0) {
          const { data: compRows } = await supabase
            .from('components')
            .select('part_number, available_qty, on_order_qty, reserved_qty, expected_qty, category')
            .in('part_number', allSkus);
          (compRows ?? []).forEach((c: any) => {
            stockFullMap[c.part_number] = {
              available: c.available_qty ?? 0,
              reserved: c.reserved_qty ?? 0,
              on_order: c.on_order_qty ?? 0,
              expected: c.expected_qty ?? 0,
              category: (c.category || '').toLowerCase(),
            };
          });
        }

        // 4. Compute max buildable
        for (const id of buildIds) {
          const bom = bomByBuild[id];
          if (!bom || bom.length === 0) { maxBuildableMap[id] = null; continue; }
          maxBuildableMap[id] = Math.min(
            ...bom.map(item => Math.floor((stockFullMap[item.sku]?.available ?? 0) / Math.max(item.qty, 1)))
          );
        }
      }

      // Count ALL completed orders per build (by build_id AND build_sku)
      const unitsBuiltMap: Record<string, number> = {};
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const recentlyBuiltIds = new Set<string>();
      // Hoisted so the APM section can reuse them
      const allOrders: any[] = [];
      const skuToId: Record<string, string> = {};

      if (buildIds.length > 0) {
        const allSkuValues = buildIds.map(id => buildSkuMap[id]).filter(Boolean);

        // Fetch all completed/Posted orders by build_id
        const { data: ordersById } = await supabase
          .from('assembly_orders')
          .select('id, build_id, build_sku, quantity, completed_at, completed_date, posted_date, status')
          .in('build_id', buildIds)
          .in('status', ['completed', 'Posted']);

        // Fetch all completed/Posted orders by build_sku (for orders without build_id)
        let ordersBySku: any[] = [];
        if (allSkuValues.length > 0) {
          const { data: skuOrders } = await supabase
            .from('assembly_orders')
            .select('id, build_id, build_sku, quantity, completed_at, completed_date, posted_date, status')
            .in('build_sku', allSkuValues)
            .in('status', ['completed', 'Posted']);
          ordersBySku = skuOrders ?? [];
        }

        // Merge and deduplicate orders by unique id
        const seenOrders = new Set<string>();
        for (const o of [...(ordersById ?? []), ...ordersBySku]) {
          if (!seenOrders.has(o.id)) {
            seenOrders.add(o.id);
            allOrders.push(o);
          }
        }

        // Build a sku-to-id lookup
        buildIds.forEach(id => { if (buildSkuMap[id]) skuToId[buildSkuMap[id]] = id; });

        // Count units and track recency
        for (const o of allOrders) {
          const resolvedId = o.build_id || skuToId[o.build_sku];
          if (!resolvedId) continue;
          const qty = o.quantity ?? 1;
          unitsBuiltMap[resolvedId] = (unitsBuiltMap[resolvedId] ?? 0) + qty;
          // Use completed_at, completed_date, or posted_date as the effective date
          const effectiveDate = o.completed_at || o.completed_date || o.posted_date;
          if (effectiveDate && new Date(effectiveDate) >= sixMonthsAgo) {
            recentlyBuiltIds.add(resolvedId);
          }
        }
      }

      // Compute effective status per build
      const effectiveStatusMap: Record<string, string> = {};
      for (const id of buildIds) {
        const bom = bomByBuild[id] ?? [];
        const keyComponents = bom.filter(item => {
          const type = item.type || stockFullMap[item.sku]?.category || '';
          return KEY_CATEGORIES.has(type);
        });
        const keyComponentsOk = keyComponents.length > 0 && keyComponents.every(item => {
          const stock = stockFullMap[item.sku];
          if (!stock) return false;
          return (stock.available + stock.reserved + stock.on_order) > 0;
        });
        const builtRecently = recentlyBuiltIds.has(id);
        effectiveStatusMap[id] = (keyComponentsOk || builtRecently) ? 'Active' : 'Inactive';
      }

      // Auto-sync status to DB where it changed
      const statusUpdates = buildIds.filter(id => {
        const current = (builds ?? []).find((b: any) => b.id === id)?.status;
        return current && effectiveStatusMap[id] && current !== effectiveStatusMap[id];
      });
      if (statusUpdates.length > 0) {
        for (const id of statusUpdates) {
          const oldStatus = (builds ?? []).find((b: any) => b.id === id)?.status;
          const newStatus = effectiveStatusMap[id];
          supabase.from('builds').update({ status: newStatus }).eq('id', id).then(() => {});
          writeAuditLog({
            action: 'build_status_changed',
            entity_type: 'build',
            entity_id: id,
            entity_label: buildSkuMap[id] ?? undefined,
            old_value: { status: oldStatus },
            new_value: { status: newStatus },
            notes: 'Auto-sync from builds list page based on component availability',
          });
        }
      }

      // Fetch APM scores — live (assembly_order_benchmarks) first, historical fallback
      const apmMap: Record<string, number | null> = {};
      if (buildIds.length > 0) {
        // Live: reuse allOrders already fetched for units_built
        if (allOrders.length > 0) {
          const allOrderIds = allOrders.map((o: any) => o.id);
          const { data: liveBench } = await supabase
            .from('assembly_order_benchmarks')
            .select('assembly_order_id, passmark_rating')
            .in('assembly_order_id', allOrderIds)
            .gt('passmark_rating', 0);
          if (liveBench && liveBench.length > 0) {
            const scoresByBuild: Record<string, number[]> = {};
            for (const b of liveBench) {
              if (!b.passmark_rating) continue;
              const order = allOrders.find((o: any) => o.id === b.assembly_order_id);
              if (!order) continue;
              const resolvedId = order.build_id || skuToId[order.build_sku];
              if (!resolvedId) continue;
              if (!scoresByBuild[resolvedId]) scoresByBuild[resolvedId] = [];
              scoresByBuild[resolvedId].push(b.passmark_rating);
            }
            for (const [bid, scores] of Object.entries(scoresByBuild)) {
              apmMap[bid] = Math.round(scores.reduce((s: number, v: number) => s + v, 0) / scores.length);
            }
          }
        }
        // Historical fallback for builds with no live benchmark data
        const needFallback = buildIds.filter(id => apmMap[id] == null);
        if (needFallback.length > 0) {
          const { data: benchRows } = await supabase
            .from('build_benchmarks')
            .select('build_id, passmark_rating')
            .in('build_id', needFallback);
          (benchRows ?? []).forEach((r: any) => { apmMap[r.build_id] = r.passmark_rating; });
        }
      }

      return (builds ?? []).map((b: any) => ({
        ...b,
        status: effectiveStatusMap[b.id] || b.status,
        units_built: unitsBuiltMap[b.id] ?? 0,
        max_buildable: maxBuildableMap[b.id] ?? null,
        apm_score: apmMap[b.id] ?? null,
        component_skus: (bomByBuild[b.id] || []).map(c => c.sku),
      }));
    },
    staleTime: 60_000,
  });

  // Stock is its own query rather than part of the builds fetch: it is derived
  // in SQL from three tables, it changes on a different clock (a sale, a posted
  // order), and keeping it separate means a slow or failing stock lookup leaves
  // the rest of the page working. A build with no stock row simply reads "—".
  const { data: stockByBuild } = useQuery({
    queryKey: ['build-stock'],
    queryFn: fetchBuildStock,
    staleTime: 60_000,
  });

  // Client-side search, filter, sort, paginate
  const withStock = (allBuildsData ?? []).map((b: any) => {
    const stock = stockByBuild?.get(b.id);
    return {
      ...b,
      on_hand:    stock?.on_hand ?? null,
      on_display: stock?.on_display ?? 0,
      available:  stock?.available ?? null,
    };
  });

  const filtered = withStock.filter((b: any) => {
    if (search) {
      const s = search.toLowerCase();
      const matchesSku = b.sku?.toLowerCase().includes(s);
      const matchesDesc = b.description?.toLowerCase().includes(s);
      const matchesComponent = (b.component_skus || []).some((cs: string) => cs.toLowerCase().includes(s));
      if (!matchesSku && !matchesDesc && !matchesComponent) return false;
    }
    if (statusFilter && b.status !== statusFilter) return false;
    return true;
  });

  const sorted = [...filtered].sort((a: any, b: any) => {
    const dir = sortOrder === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'sku': return dir * (a.sku || '').localeCompare(b.sku || '');
      case 'description': return dir * (a.description || '').localeCompare(b.description || '');
      case 'status': return dir * (a.status || '').localeCompare(b.status || '');
      case 'units_built': return dir * ((a.units_built ?? 0) - (b.units_built ?? 0));
      // -1 for a build with no opening balance, so "never synced" sorts below
      // "none in stock" instead of landing among the well-stocked ones.
      case 'in_stock': return dir * ((a.on_hand ?? -1) - (b.on_hand ?? -1));
      case 'max_buildable': return dir * ((a.max_buildable ?? -1) - (b.max_buildable ?? -1));
      case 'apm_score': return dir * ((a.apm_score ?? -1) - (b.apm_score ?? -1));
      case 'date_created': return dir * ((a.date_created || '').localeCompare(b.date_created || ''));
      default: return 0;
    }
  });

  // Store full sorted list for prev/next navigation on detail page
  useListNavigationStore('builds', sorted.map((b: any) => b.id));

  const totalCount = sorted.length;
  const totalPages = pageSize === 'all' ? 1 : Math.ceil(totalCount / (pageSize as number));
  const data = pageSize === 'all'
    ? { data: sorted, count: totalCount }
    : { data: sorted.slice((page - 1) * (pageSize as number), page * (pageSize as number)), count: totalCount };


  const handleSort = (field: SortField) => {
    if (sortField === field) {
      updateParams({ order: sortOrder === 'asc' ? 'desc' : null, page: null });
    } else {
      updateParams({ sort: field === 'sku' ? null : field, order: null, page: null });
    }
  };

  const handleSearchChange = debounce((value: string) => {
    updateParams({ q: value || null, page: null });
  }, 300);

  const SortHeader = ({ field, children, className = '' }: { field: SortField; children: React.ReactNode; className?: string }) => (
    <th
      className={cn('text-left p-3 font-medium cursor-pointer hover:bg-muted/70 select-none whitespace-nowrap', className)}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={`h-3 w-3 ${sortField === field ? 'text-primary' : 'text-muted-foreground'}`} />
      </div>
    </th>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Builds</h1>
          <p className="text-muted-foreground">{totalCount} build configurations</p>
        </div>
        <Button asChild>
          <Link to="/configurator">
            <Plus className="mr-2 h-4 w-4" />
            Create Build
          </Link>
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by SKU, description, or component…"
                className="pl-9 pr-9"
                value={searchInput}
                onChange={e => { const v = e.target.value; setSearchInput(v); handleSearchChange(v); }}
              />
              {searchInput && (
                <button onClick={() => { setSearchInput(''); updateParams({ q: null, page: null }); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <select
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
              value={statusFilter}
              onChange={e => updateParams({ status: e.target.value || null, page: null })}
            >
              <option value="">All Status</option>
              {STATUS_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-muted-foreground">Show:</span>
              <select
                className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={pageSize}
                onChange={e => {
                  const v = e.target.value;
                  updateParams({ pageSize: v === '25' ? null : v, page: null });
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Builds Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <SortHeader field="sku">SKU</SortHeader>
                      <SortHeader field="description">Description</SortHeader>
                      <SortHeader field="status">Status</SortHeader>
                      <SortHeader field="units_built" className="text-center p-3">Units Built</SortHeader>
                      <SortHeader field="in_stock" className="text-center p-3">In Stock</SortHeader>
                      <SortHeader field="max_buildable" className="text-center p-3">Max Buildable</SortHeader>
                      <SortHeader field="apm_score">APM</SortHeader>
                      <SortHeader field="date_created">Date Created</SortHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.data.map((build: any) => (
                      <tr key={build.id} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="p-3">
                          <Link
                            to={`/builds/${build.id}`}
                            className="font-mono text-xs text-primary hover:underline whitespace-nowrap"
                          >
                            {build.sku}
                          </Link>
                        </td>
                        <td className="p-3 max-w-[400px]">
                          <p className="truncate" title={build.description}>
                            {build.description || '—'}
                          </p>
                          {build.comments && (
                            <p className="text-xs text-muted-foreground truncate mt-0.5" title={build.comments}>
                              {build.comments}
                            </p>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge className={cn('text-xs', getStatusColor(build.status))}>
                            {build.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-center">
                          <span className="font-medium">{build.units_built ?? '—'}</span>
                        </td>
                        <td className="p-3 text-center">
                          {build.on_hand == null ? (
                            // Never synced. A dash, because showing 0 would be a
                            // wrong answer that looks exactly like a real one.
                            <span className="text-muted-foreground">—</span>
                          ) : (
                            <span className="inline-flex items-baseline gap-1">
                              <span className={cn('font-medium', build.on_hand === 0 && 'text-muted-foreground')}>
                                {build.on_hand}
                              </span>
                              {build.on_display > 0 && (
                                <span
                                  className="text-xs text-muted-foreground"
                                  title={`${build.on_display} on demo stations; ${build.available} available to sell`}
                                >
                                  ({build.on_display} on display)
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <span className={cn('font-medium',
                            build.max_buildable === 0 ? 'text-red-600' :
                            build.max_buildable != null && build.max_buildable <= 3 ? 'text-yellow-600' :
                            ''
                          )}>
                            {build.max_buildable != null ? build.max_buildable : '—'}
                          </span>
                        </td>
                        <td className="p-3">
                          {build.apm_score != null ? (
                            <APMBadge passmarkRating={build.apm_score} compact />
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="p-3 text-sm text-muted-foreground whitespace-nowrap">
                          {build.date_created
                            ? new Date(build.date_created).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!data?.data.length) && (
                  <div className="flex items-center justify-center h-32 text-muted-foreground">
                    No builds found
                  </div>
                )}
              </div>

              {/* Pagination */}
              {pageSize !== 'all' && totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <div className="text-sm text-muted-foreground">
                    Showing {((page - 1) * (pageSize as number)) + 1}–{Math.min(page * (pageSize as number), totalCount)} of {totalCount}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="icon" onClick={() => updateParams({ page: null })} disabled={page === 1}>
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => updateParams({ page: page - 1 <= 1 ? null : String(page - 1) })} disabled={page === 1}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="px-3 text-sm">Page {page} of {totalPages}</span>
                    <Button variant="outline" size="icon" onClick={() => updateParams({ page: String(page + 1) })} disabled={page === totalPages}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => updateParams({ page: String(totalPages) })} disabled={page === totalPages}>
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              {pageSize === 'all' && totalCount > 0 && (
                <div className="px-4 py-3 border-t text-sm text-muted-foreground">
                  Showing all {totalCount} builds
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
